/**
 * How soaked ground looks (ADR-144 build note, P2.27-T20): the whole world, ground, walls, roofs, trees and props alike, is
 * darkened by a share, shifted cool and slightly desaturated, as one colour matrix that grows with the soak. The matrix is
 * written into a caller's array, so a frame allocates nothing.
 */

/**
 * The share of its brightness the world loses when soaked.
 */
export const SOAK_DARKEN = 0.22;

/**
 * How far the cool shift moves the red and blue channels apart: red is scaled by `1 - SOAK_COOL`, blue by `1 + SOAK_COOL`.
 */
export const SOAK_COOL = 0.06;

/**
 * The share of its colour a soaked world loses, moving each channel toward its luminance.
 */
export const SOAK_DESATURATE = 0.12;

const LUMA: readonly number[] = [0.2126, 0.7152, 0.0722];
const SCALE: readonly number[] = [(1 - SOAK_DARKEN) * (1 - SOAK_COOL), 1 - SOAK_DARKEN, (1 - SOAK_DARKEN) * (1 + SOAK_COOL)];

/**
 * The 4 by 5 colour matrix of a soak, row by row (red, green, blue, alpha), the last column the offset.
 *
 * @param soak 0 for dry ground to 1 for soaked.
 * @param out The 20 values to write.
 * @returns `out`.
 */
export function soakMatrixInto<T extends number[] | Float32Array>(soak: number, out: T): T {
  const s = soak <= 0 ? 0 : soak >= 1 ? 1 : soak;
  const keep = 1 - SOAK_DESATURATE;
  for (let row = 0; row < 3; row += 1) {
    const full = SCALE[row] as number;
    for (let column = 0; column < 3; column += 1) {
      const target = full * ((row === column ? keep : 0) + SOAK_DESATURATE * (LUMA[column] as number));
      const dry = row === column ? 1 : 0;
      out[row * 5 + column] = dry + (target - dry) * s;
    }
    out[row * 5 + 3] = 0;
    out[row * 5 + 4] = 0;
  }
  out[15] = 0;
  out[16] = 0;
  out[17] = 0;
  out[18] = 1;
  out[19] = 0;
  return out;
}
